import type { Theme } from '@/contexts/theme';

import { getAppliedTheme } from './appliedTheme';
import {
    CHART_FONT_FALLBACK,
    CHART_STRONG_FONT,
    CHART_TOKEN_FALLBACKS,
    CHART_TOKEN_NAMES,
    type ChartTokenName,
    PALETTE_LIFT_TOKENS,
    SERIES_TOKEN_NAMES,
} from './chartTokens';
import type { ChartTheme } from './types';

const HEX6 = /^#[0-9a-f]{6}$/;
const HEX3 = /^#([0-9a-f])([0-9a-f])([0-9a-f])$/;

/** `value` as lower-case 6-digit hex, or null when it is not a hex colour. */
export function toHex6(value: string): string | null {
    const trimmed = value.trim().toLowerCase();
    if (HEX6.test(trimmed)) return trimmed;
    const short = HEX3.exec(trimmed);
    return short ? `#${short[1]}${short[1]}${short[2]}${short[2]}${short[3]}${short[3]}` : null;
}

/**
 * Reads the chart tokens from the computed style of `scope` into a frozen `ChartTheme`.
 *
 * A token the stylesheet does not define, or defines as something other than a hex colour,
 * falls back to its light value, so a chart always receives a colour it can parse.
 */
export function readChartTheme(
    mode: Theme = getAppliedTheme(),
    scope: Element = document.documentElement
): ChartTheme {
    const computed = getComputedStyle(scope);
    const values = new Map<ChartTokenName, string>(
        CHART_TOKEN_NAMES.map((token) => [
            token,
            toHex6(computed.getPropertyValue(`--${token}`)) ?? CHART_TOKEN_FALLBACKS[token],
        ])
    );
    const resolve = (token: ChartTokenName): string =>
        values.get(token) ?? CHART_TOKEN_FALLBACKS[token];
    const fontFamily = computed.getPropertyValue('--regular-font').trim() || CHART_FONT_FALLBACK;

    return Object.freeze({
        mode,
        text: resolve('chart-text'),
        axis: resolve('chart-axis'),
        grid: resolve('chart-grid'),
        surface: resolve('chart-surface'),
        labelInk: resolve('chart-label-ink'),
        tooltip: Object.freeze({
            bg: resolve('chart-tooltip-bg'),
            text: resolve('chart-tooltip-text'),
            border: resolve('chart-tooltip-border'),
        }),
        zoom: Object.freeze({
            fill: resolve('chart-zoom-fill'),
            border: resolve('chart-zoom-border'),
        }),
        barInk: Object.freeze({
            dark: resolve('chart-bar-ink-dark'),
            light: resolve('chart-bar-ink-light'),
        }),
        series: Object.freeze(SERIES_TOKEN_NAMES.map(resolve)),
        fontFamily,
        strongFontFamily: CHART_STRONG_FONT,
        resolve,
        color: (paletteColor: string): string => {
            const key = paletteColor.toLowerCase();
            const token = PALETTE_LIFT_TOKENS[key];
            if (!token) return paletteColor;
            // Where the theme draws the colour unchanged, hand back the caller's own string,
            // so the light theme is an exact no-op.
            const lifted = resolve(token);
            return lifted === key ? paletteColor : lifted;
        },
    });
}

let cached: { theme: ChartTheme; tokensLoaded: boolean } | null = null;

/**
 * The document's chart theme for the applied theme, read once per switch. Every chart on the
 * page shares the one object, which is what lets it serve as a memo dependency.
 *
 * In dev, Vite injects the token stylesheet as its module evaluates, which can be after a chart
 * module first asks for the theme. A read without the tokens is all fallbacks, so it is only
 * kept until the tokens appear, never for the rest of the session.
 */
export function getDocumentChartTheme(): ChartTheme {
    const mode = getAppliedTheme();
    const tokensLoaded =
        getComputedStyle(document.documentElement).getPropertyValue('--chart-grid').trim() !== '';
    if (!cached || cached.theme.mode !== mode || cached.tokensLoaded !== tokensLoaded) {
        cached = { theme: readChartTheme(mode), tokensLoaded };
    }
    return cached.theme;
}
